import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Input, Table, Button, Spin } from "antd";
import Highlighter from "react-highlight-words";
import SearchPanel from "./SearchPanel";
import { onFilter } from "../../helpers/bookingRecordsHelper";
import Calendar from "../shared/Calendar/Calendar";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBookingRecord,
  getBookingRecords,
  getBookingRecordsTotal,
  downloadInvoice,
  downloadCsv,
} from "../../dux/bookingRecords";
import { formatDate } from "../../helpers/dateHelper";

import styles from "./BookingRecords.module.css";
import CustomizedSelects from "./customizedSelect.button";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Tooltip } from "@mui/material";
import { showPrompt } from "../../dux/prompt";
import { promptCategoryType } from "../../helpers/constants";
import dayjs from "dayjs";
import { isAuthorized, getUser } from "../../helpers/sessionStorageHelper";
import { isCsvDownloadPending, isApiPending } from "../../dux/apiStatus";
import {
  DownloadDoneRounded,
  EditAttributesRounded,
  EditOutlined,
} from "@mui/icons-material";
import EditBookingModal from "./EditBooking/EditBookingModal";

const { Column } = Table;

function getAllowedLocations() {
  const user = getUser();
  return user && user.locations ? user.locations : [];
}

function getInitialBookingIdFromUrl() {
  try {
    return (new URLSearchParams(window.location.search).get("booking_id") || "").trim();
  } catch {
    return "";
  }
}

const DEEP_LINK_DATE_RANGE_DAYS_BACK = 365;
const DEEP_LINK_DATE_RANGE_DAYS_FORWARD = 90;

const BookingRecords = () => {
  const allowedLocations = getAllowedLocations();
  const [searchParams, setSearchParams] = useSearchParams();
  const bookingIdParam = (searchParams.get("booking_id") || "").trim();
  const locationParam = (searchParams.get("location") || "").trim();
  const [locationFilter, setLocationFilter] = useState(() => {
    if (locationParam) return locationParam;
    return allowedLocations[0] || "";
  });

  const dispatch = useDispatch();
  const [phoneNo, setphoneNo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const bid = getInitialBookingIdFromUrl();
    return bid
      ? dayjs().subtract(DEEP_LINK_DATE_RANGE_DAYS_BACK, "day")
      : dayjs();
  });
  const [tillDate, setTillDate] = useState(() => {
    const bid = getInitialBookingIdFromUrl();
    return bid
      ? dayjs().add(DEEP_LINK_DATE_RANGE_DAYS_FORWARD, "day")
      : dayjs();
  });
  const [searchedColumn, setSearchedColumn] = useState("");

  const searchInput = useRef(null);
  const list = useSelector(getBookingRecords);
  const total = useSelector(getBookingRecordsTotal);
  const isCsvDownloading = useSelector(isCsvDownloadPending);
  const isLoading = useSelector((state) =>
    isApiPending(state, "fetchBookingRecord")
  );

  const [sortField, setSortField] = React.useState("playingTimestamp");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 🔎 track which column's filter dropdown just opened (to focus input)
  const [openFilterFor, setOpenFilterFor] = useState(null);

  // Edit booking modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const hasAppliedLocationParamRef = useRef(false);
  const prevBookingIdParamRef = useRef(undefined);

  useLayoutEffect(() => {
    if (!openFilterFor) return;
    const el = searchInput.current;
    if (el) {
      try {
        // antd Input forwards focus correctly
        el.focus({ cursor: "all" });
        if (typeof el.select === "function") el.select();
      } catch {}
    }
  }, [openFilterFor]);

  const buildFetchPayload = React.useCallback(
    ({
      pageOverride,
      limitOverride,
      sortOverride,
      locationOverride,
      omitBookingId = false,
    } = {}) => {
      const bookingId = omitBookingId ? undefined : bookingIdParam || undefined;
      const resolvedLocation =
        locationOverride ??
        locationFilter ??
        locationParam ??
        allowedLocations[0] ??
        undefined;
      const location = resolvedLocation || undefined;

      return {
        startDate: fromDate && formatDate(fromDate.format(), 3),
        endDate: tillDate && formatDate(tillDate.format(), 3),
        mobileNumber: phoneNo,
        sortField: sortOverride || sortField,
        location,
        page: pageOverride ?? currentPage,
        limit: limitOverride ?? pageSize,
        bookingId,
      };
    },
    [
      bookingIdParam,
      fromDate,
      tillDate,
      phoneNo,
      sortField,
      locationFilter,
      locationParam,
      allowedLocations,
      currentPage,
      pageSize,
    ]
  );

  const handleBookingIdToggle = React.useCallback(
    (e, recordBookingId) => {
      e.stopPropagation();
      const id =
        recordBookingId != null && recordBookingId !== ""
          ? String(recordBookingId).trim()
          : "";
      if (!id) return;
      const nextParams = new URLSearchParams(searchParams);
      if (id === bookingIdParam) {
        nextParams.delete("booking_id");
        setSearchParams(nextParams);
        setCurrentPage(1);
        dispatch(
          fetchBookingRecord(
            buildFetchPayload({ pageOverride: 1, omitBookingId: true })
          )
        );
      } else {
        nextParams.set("booking_id", id);
        setSearchParams(nextParams);
      }
    },
    [
      searchParams,
      setSearchParams,
      bookingIdParam,
      dispatch,
      buildFetchPayload,
    ]
  );

  const handleLocationChange = (e) => {
    const selected = e.target.value;
    setLocationFilter(selected);
    setCurrentPage(1);
    dispatch(fetchBookingRecord(buildFetchPayload({ pageOverride: 1, locationOverride: selected })));
  };

  const handleChange = (sortFieldChange) => {
    setSortField(sortFieldChange.target.value);
    setCurrentPage(1);
    dispatch(
      fetchBookingRecord(
        buildFetchPayload({ pageOverride: 1, sortOverride: sortFieldChange.target.value })
      )
    );
  };

  const phoneChangeHandler = ({ target: { value } }) => {
    if (value.length <= 10) setphoneNo(value);
  };

  const searchClickHandler = () => {
    setCurrentPage(1);
    dispatch(fetchBookingRecord(buildFetchPayload({ pageOverride: 1 })));
  };

  const downloadCsvHandler = () => {
    dispatch(
      downloadCsv({
        startDate: fromDate && formatDate(fromDate.format(), 3),
        endDate: tillDate && formatDate(tillDate.format(), 3),
        mobileNumber: phoneNo,
        sortField: sortField,
        location: locationFilter || allowedLocations[0] || undefined,
      })
    );
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <SearchPanel
        setSelectedKeys={setSelectedKeys}
        selectedKeys={selectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
        handleSearch={handleSearch}
        dataIndex={dataIndex}
        searchInput={searchInput}
        handleReset={handleReset}
        setSearchText={setSearchText}
        setSearchedColumn={setSearchedColumn}
      />
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => onFilter(dataIndex, value, record),
    filterDropdownProps: {
      onOpenChange: (open) => setOpenFilterFor(open ? dataIndex : null),
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const locationDisplay = {
    srinagar: "Sonwar Bagh, Srinagar (J&K)",
    jammu: "KC Sports Club, Paloura, Akhnoor Road, Jammu (J&K)",
    // baramulla: "SSM College, Pattan (J&K)",
  };

  const sportDisplay = {
    football: "Football",
    "box-cricket": "Box Cricket",
    "cricket-net": "Cricket Net",
    "pickle-ball": "Pickleball",
  };

  const [windowDimenion, detectHW] = useState({
    winWidth: window.innerWidth,
    winHeight: window.innerHeight,
  });

  const detectSize = () => {
    detectHW({
      winWidth: window.innerWidth,
      winHeight: window.innerHeight,
    });
  };

  // initial fetch
  useEffect(() => {
    dispatch(fetchBookingRecord(buildFetchPayload({})));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // run once on mount

  useEffect(() => {
    if (!locationParam || hasAppliedLocationParamRef.current) return;
    setLocationFilter(locationParam);
    hasAppliedLocationParamRef.current = true;
  }, [locationParam]);

  // Deep link (?booking_id=): widen date range so the booking is not excluded by default "today" filter.
  // Initial load uses lazy fromDate/tillDate above; this handles SPA navigations to a new booking_id.
  useEffect(() => {
    const prev = prevBookingIdParamRef.current;

    if (!bookingIdParam) {
      prevBookingIdParamRef.current = bookingIdParam;
      return;
    }

    if (prev === undefined) {
      prevBookingIdParamRef.current = bookingIdParam;
      return;
    }

    if (prev === bookingIdParam) {
      return;
    }

    prevBookingIdParamRef.current = bookingIdParam;

    const from = dayjs().subtract(DEEP_LINK_DATE_RANGE_DAYS_BACK, "day");
    const to = dayjs().add(DEEP_LINK_DATE_RANGE_DAYS_FORWARD, "day");
    setFromDate(from);
    setTillDate(to);
    setCurrentPage(1);

    const resolvedLocation =
      locationFilter || locationParam || allowedLocations[0] || undefined;

    dispatch(
      fetchBookingRecord({
        startDate: formatDate(from.format(), 3),
        endDate: formatDate(to.format(), 3),
        mobileNumber: phoneNo,
        sortField,
        location: resolvedLocation || undefined,
        page: 1,
        limit: pageSize,
        bookingId: bookingIdParam,
      })
    );
  }, [
    bookingIdParam,
    dispatch,
    phoneNo,
    sortField,
    locationFilter,
    locationParam,
    allowedLocations,
    pageSize,
  ]);

  // window resize listener — register once
  useEffect(() => {
    window.addEventListener("resize", detectSize);
    return () => {
      window.removeEventListener("resize", detectSize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = (record) => {
    dispatch(downloadInvoice({ id: record.bookingId }));
  };

  const handleEdit = (totalAmount, bookingId) => {
    const id = bookingId != null && bookingId !== "" ? String(bookingId) : "";
    if (!id) return;
    let total_amount_dec = parseInt(String(totalAmount).split(" ")[0], 10);
    dispatch(
      showPrompt({
        category: promptCategoryType.BOOKING_EDIT,
        data: {
          ...buildFetchPayload({}),
          total_amount: total_amount_dec,
          bookingId: id,
        },
      })
    );
  };

  const handleEditBooking = (record) => {
    setSelectedBooking(record);
    setEditModalVisible(true);
  };

  const handleEditModalClose = () => {
    setEditModalVisible(false);
    setSelectedBooking(null);
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedBooking(null);
    // Refresh the booking records
    searchClickHandler();
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    dispatch(fetchBookingRecord(buildFetchPayload({ pageOverride: page, limitOverride: size })));
  };

  useEffect(() => {
    if (!bookingIdParam) {
      setExpandedRowKeys([]);
      return;
    }
    const match = list.find((record) => record.bookingId === bookingIdParam);
    if (match) {
      setExpandedRowKeys([match.bookingId]);
    }
  }, [bookingIdParam, list]);

  const renderExpandedEquipment = (record) => {
    if (!record.booking_equipment || record.booking_equipment.length === 0) {
      return (
        <div className={styles.equipmentDetails}>
          <h4>Equipment Details</h4>
          <div
            style={{
              textAlign: "center",
              color: "#666",
              padding: "20px",
              fontStyle: "italic",
            }}
          >
            No equipment was added to this booking.
          </div>
        </div>
      );
    }

    return (
      <div className={styles.equipmentDetails}>
        <h4>Equipment Details</h4>
        <div className={styles.equipmentGrid}>
          {record.booking_equipment.map((equipment, index) => (
            <div key={equipment.id || index} className={styles.equipmentItem}>
              <div className={styles.equipmentField}>
                <strong>Equipment:</strong>
                <div className={styles.equipmentFieldValue}>
                  {equipment.equipment_config?.equipment_name ||
                    "Unknown Equipment"}
                </div>
              </div>
              <div className={styles.equipmentField}>
                <strong>Type:</strong>
                <div className={styles.equipmentFieldValue}>
                  {equipment.equipment_config?.equipment_type || "Unknown Type"}
                </div>
              </div>
              <div className={styles.equipmentField}>
                <strong>Quantity:</strong>
                <div className={styles.equipmentFieldValue}>
                  {equipment.quantity || 0}
                </div>
              </div>
              <div className={styles.equipmentField}>
                <strong>Unit Price:</strong>
                <div className={styles.equipmentFieldValue}>
                  ₹{equipment.unit_price || 0}
                </div>
              </div>
              <div className={styles.equipmentField}>
                <strong>Total Price:</strong>
                <div
                  className={`${styles.equipmentFieldValue} ${styles.equipmentTotalPrice}`}
                >
                  ₹{equipment.total_price || 0}
                </div>
              </div>
              {equipment.equipment_config?.description && (
                <div className={styles.equipmentDescription}>
                  <strong>Description:</strong>
                  <div className={styles.equipmentDescriptionText}>
                    {equipment.equipment_config.description}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        {record.equipment_total && record.equipment_total > 0 && (
          <div className={styles.equipmentTotalAmount}>
            <strong>Total Equipment Amount: ₹{record.equipment_total}</strong>
          </div>
        )}
      </div>
    );
  };

  const getNotesText = (rec) =>
    (rec?.notes ?? rec?.note ?? "").toString().trim();

  return (
    <div className={styles.container}>
      <div className={styles.filterContainer}>
        <div className={styles.mobileContainer}>
          <span className={styles.text}>Location:</span>
          <Tooltip
            title={locationDisplay[locationFilter] || locationFilter}
            arrow
            placement="top"
          >
            <select
              value={locationFilter}
              onChange={handleLocationChange}
              className={styles.inputField}
            >
              {allowedLocations.map((loc) => (
                <option key={loc} value={loc}>
                  {locationDisplay[loc] || loc}
                </option>
              ))}
            </select>
          </Tooltip>
        </div>

        <div className={styles.calendarContainer}>
          <span className={styles.text}>From Date:</span>
          <div className={styles.calendar}>
            <Calendar date={fromDate} setDate={setFromDate} />
          </div>
        </div>
        <div className={styles.calendarContainer}>
          <span className={styles.text}>To Date:</span>
          <div className={styles.calendar}>
            <Calendar date={tillDate} setDate={setTillDate} />
          </div>
        </div>
        <div className={styles.mobileContainer}>
          <span className={styles.text}>Enter Mobile No:</span>
          <Input
            value={phoneNo}
            type="number"
            onChange={phoneChangeHandler}
            className={styles.inputField}
            placeholder="Mobile No"
          />
        </div>
        <div className={styles.searchBtnContainer}>
          <ToggleButtonGroup
            color="primary"
            value={sortField}
            exclusive
            onChange={handleChange}
            aria-label="Platform"
          >
            <ToggleButton value="bookingTimestamp">Booking Date</ToggleButton>
            <ToggleButton value="playingTimestamp">Playing Date</ToggleButton>
          </ToggleButtonGroup>
          <button className={styles.searchBtn} onClick={searchClickHandler}>
            Search
          </button>
          <button
            className={styles.downloadBtn}
            onClick={downloadCsvHandler}
            disabled={isCsvDownloading}
          >
            {isCsvDownloading ? "Downloading..." : "Download CSV"}
          </button>
        </div>
      </div>

      <div className={styles.table}>
        <div className={styles.tableWrapper}>
          <div className={styles.expandableHint}>
            <span className={styles.expandableHintIcon}>💡</span>
            <span>
              Click a row to expand equipment details. Click a Booking Id to
              filter by that booking; click the same Id again to clear the
              filter.
            </span>
          </div>

          {isLoading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "40px",
                minHeight: "300px",
                background: "#f9f9f9",
                borderRadius: "8px",
              }}
            >
              <Spin size="large" />
              <div
                style={{ marginTop: "16px", color: "#666", fontSize: "16px" }}
              >
                Loading booking records...
              </div>
            </div>
          ) : list && list.length > 0 ? (
            <Table
              bordered={false}
              dataSource={list}
              id="bookingId"
              expandable={{
                expandedRowRender: renderExpandedEquipment,
                expandRowByClick: true,
                expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
                expandIcon: ({ expanded, onExpand, record }) => (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                    style={{ cursor: "pointer", color: "#1890ff" }}
                  >
                    {expanded ? "▼" : "▶"}
                  </span>
                ),
              }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (t, range) =>
                  `${range[0]}-${range[1]} of ${t} items`,
                pageSizeOptions: ["10", "20", "50", "100"],
                onChange: handlePageChange,
                onShowSizeChange: handlePageChange,
              }}
              rowKey="bookingId"
              size="small"
              rowClassName={(record) =>
                record.bookingId === bookingIdParam ? styles.highlightedRow : ""
              }
              scroll={{
                y:
                  windowDimenion.winWidth < 768
                    ? "calc(100vh - 400px)"
                    : "calc(100vh - 350px)",
                x: windowDimenion.winWidth < 768 ? 1200 : "max-content",
              }}
            >
              <Column
                className={styles.rowCount}
                title="No."
                key="rowCount"
                fixed={windowDimenion.winWidth > 768}
                width={50}
                render={(text, record, index) => (
                  <div className={styles.expandableRowNumber}>
                    <span>{index + 1}</span>
                    <span className={styles.expandableIndicator}>▶</span>
                  </div>
                )}
              />
              <Column
                className={styles.bookingId}
                title="Booking Id"
                dataIndex="bookingId"
                fixed={windowDimenion.winWidth > 768}
                width={300}
                {...getColumnSearchProps("bookingId")}
                render={(text, record) => {
                  const label =
                    searchedColumn === "bookingId" ? (
                      <Highlighter
                        highlightStyle={{
                          backgroundColor: "#ffc069",
                          padding: 0,
                        }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ""}
                      />
                    ) : (
                      text
                    );
                  const isActive =
                    record?.bookingId != null &&
                    String(record.bookingId) === bookingIdParam;
                  return (
                    <button
                      type="button"
                      className={
                        isActive
                          ? `${styles.bookingIdToggle} ${styles.bookingIdToggleActive}`
                          : styles.bookingIdToggle
                      }
                      aria-pressed={isActive}
                      onClick={(e) =>
                        handleBookingIdToggle(e, record.bookingId)
                      }
                    >
                      {label}
                    </button>
                  );
                }}
              />
              <Column
                className={styles.location}
                title="Location"
                dataIndex="location"
                fixed={windowDimenion.winWidth > 768}
                width={220}
                render={(text) => locationDisplay[text] || "None"}
              />
              <Column
                className={styles.name}
                title="Customer Name"
                dataIndex="name"
                fixed={windowDimenion.winWidth > 768}
                width={200}
                {...getColumnSearchProps("name")}
              />
              <Column
                className={styles.transactionId}
                title="Transaction Id"
                dataIndex="transactionId"
                width={190}
                {...getColumnSearchProps("transactionId")}
              />
              <Column
                className={styles.mobileNo}
                title="Contact No"
                dataIndex="mobileNo"
                width={120}
                {...getColumnSearchProps("mobileNo")}
              />
              <Column
                className={styles.turfNo}
                title="Sport"
                dataIndex="sport"
                width={100}
                render={(text) => sportDisplay[text] || text}
              />
              <Column
                className={styles.turfNo}
                title="Turf"
                dataIndex="turf"
                width={80}
              />
              <Column
                className={styles.bookingDate}
                title="Booking Date"
                dataIndex="bookingDate"
                width={100}
              />
              <Column
                className={styles.playingDate}
                title="Playing Date"
                dataIndex="playingDate"
                width={100}
              />
              <Column
                className={styles.startTime}
                title="Start Time"
                dataIndex="startTime"
                width={100}
              />
              <Column
                className={styles.duration}
                title="Duration"
                dataIndex="duration"
                width={80}
              />
              <Column
                className={styles.totalAmount}
                title="Total Amount"
                dataIndex="totalAmount"
                width={100}
              />
              <Column
                className={styles.amountPaid}
                title="Amount Paid"
                dataIndex="amountPaid"
                width={100}
              />
              <Column
                className={styles.remainingAmount}
                title="Remaining Amount"
                dataIndex="remainingAmount"
                width={100}
              />
              <Column
                className={styles.bookingStatus}
                title="Booking Status"
                width={110}
                render={(text, record) => (
                  <CustomizedSelects
                    defaultStatus={record.bookingStatus}
                    bookingId={record.bookingId}
                    remainingAmount={record.remainingAmount}
                    handler={searchClickHandler}
                  />
                )}
              />
              <Column
                className={styles.notes}
                title="Notes"
                key="notes"
                width={200}
                render={(text, record) => {
                  const notesText = getNotesText(record);
                  return notesText ? (
                    <Tooltip
                      title={
                        <div style={{ whiteSpace: "pre-wrap", maxWidth: 360 }}>
                          {notesText}
                        </div>
                      }
                      arrow
                      placement="top"
                    >
                      <span>{record.notes || "-"}</span>
                    </Tooltip>
                  ) : (
                    <span style={{ color: "#ccc" }}>—</span>
                  );
                }}
              />
              {(isAuthorized("SUPER_ADMIN") || isAuthorized("ADMIN")) && (
                <Column
                  className={styles.download}
                  title="Invoice Download"
                  dataIndex=""
                  key="download"
                  width={100}
                  render={(text, record) => (
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<DownloadDoneRounded />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(record);
                      }}
                    />
                  )}
                />
              )}

              {isAuthorized("SUPER_ADMIN") && (
                <Column
                  className={styles.download}
                  title="Edit Total Amount"
                  dataIndex=""
                  key="edit"
                  width={100}
                  render={(text, record) => (
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<EditAttributesRounded />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(record.totalAmount, record.bookingId);
                      }}
                      className={styles.editButton}
                    />
                  )}
                />
              )}
              {(isAuthorized("SUPER_ADMIN") || isAuthorized("ADMIN")) && (
                <Column
                  className={styles.editBooking}
                  title="Edit Booking"
                  dataIndex=""
                  key="edit_booking"
                  width={100}
                  render={(text, record) => (
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditBooking(record);
                      }}
                      className={styles.editBookingButton}
                    />
                  )}
                />
              )}
            </Table>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: "40px",
                minHeight: "300px",
                background: "#f9f9f9",
                borderRadius: "8px",
              }}
            >
              <div
                style={{ color: "#666", fontSize: "16px", textAlign: "center" }}
              >
                No booking records found for the selected criteria.
                <br />
                Try adjusting your search filters.
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Add the EditBookingModal */}
      <EditBookingModal
        visible={editModalVisible}
        onClose={handleEditModalClose}
        booking={selectedBooking}
        onSuccess={handleEditSuccess}
        refresh={{
          startDate: fromDate && formatDate(fromDate.format(), 3),
          endDate: tillDate && formatDate(tillDate.format(), 3),
          mobileNumber: phoneNo,
          sortField: sortField,
          location: locationFilter || allowedLocations[0] || undefined,
          page: currentPage,
          limit: pageSize,
        }}
      />
      <div className="footer"></div>
    </div>
  );
};

export default BookingRecords;
